import api from './api';
import { useAuthStore } from '../stores/authStore';

/**
 * Web Push subscription for the browser.
 *
 * In-page notifications (lib/notifications.ts) only fire while a tab is open
 * and unfocused. Web Push is what reaches the user when ClinikChat is closed
 * entirely — the service worker wakes up and shows the notification.
 *
 * Platform note: iOS Safari only delivers Web Push to a PWA that has been
 * added to the Home Screen (16.4+). In an ordinary iPhone Safari tab
 * `PushManager` is absent, so `subscribeToWebPush()` reports 'unsupported'
 * and callers should surface the Add to Home Screen hint instead of an error.
 */

export type WebPushResult =
  | 'subscribed'
  | 'unsupported'
  | 'permission-denied'
  | 'not-configured'
  | 'failed';

/**
 * VAPID keys travel as base64url; PushManager wants raw bytes.
 *
 * Returns an ArrayBuffer rather than a Uint8Array: under TS 5.7 a Uint8Array is
 * typed over ArrayBufferLike, which is not assignable to the DOM's BufferSource
 * (it excludes SharedArrayBuffer). Same narrowing issue as deriveKEK in
 * @clinikchat/shared crypto.ts.
 */
function urlBase64ToArrayBuffer(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buffer;
}

export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Idempotent: reuses an existing browser subscription when present, and the
 * server upserts, so calling this on every app load is safe.
 */
export async function subscribeToWebPush(): Promise<WebPushResult> {
  if (!isWebPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'permission-denied';

  try {
    const { data } = await api.get<{ success: true; data: { publicKey: string | null } }>(
      '/api/v1/users/me/web-push/public-key',
    );
    const publicKey = data.data?.publicKey;
    if (!publicKey) return 'not-configured';

    const registration = await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return 'permission-denied';
      }
      subscription = await registration.pushManager.subscribe({
        // Required by Chrome: we never send silent pushes.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(publicKey),
      });
    }

    await api.post('/api/v1/users/me/web-push', subscription.toJSON());
    return 'subscribed';
  } catch {
    // Never block the app on notification setup.
    return 'failed';
  }
}

/**
 * Upper bound for the best-effort teardown. Callers await it BEFORE clearing
 * the local session, and `navigator.serviceWorker.ready`, the DELETE or
 * `unsubscribe()` can each hang indefinitely; sign-out must never wait on that.
 */
export const WEB_PUSH_TEARDOWN_TIMEOUT_MS = 4000;

/** Best-effort teardown so a signed-out browser stops receiving pushes. */
export async function unsubscribeFromWebPush(): Promise<void> {
  if (!isWebPushSupported()) return;
  // When the timeout wins the teardown must STOP, not just be ignored: if it
  // resumed after the user signed back in it could delete the new session's
  // subscription row or unsubscribe the browser subscription they reused.
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve();
    }, WEB_PUSH_TEARDOWN_TIMEOUT_MS);
  });
  try {
    await Promise.race([teardownWebPushSubscription(controller.signal), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

async function teardownWebPushSubscription(signal: AbortSignal): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.ready;
    if (signal.aborted) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription || signal.aborted) return;
    await api
      .delete('/api/v1/users/me/web-push', { data: { endpoint: subscription.endpoint }, signal })
      .catch(() => undefined);
    if (signal.aborted) return;
    const unsubscribing = subscription.unsubscribe();
    // The browser call cannot be cancelled. If it outlives the timeout and
    // then removes the subscription, a session that started in the meantime
    // may have reused (and registered) exactly that subscription, so it would
    // silently stop receiving pushes. Re-register for whoever is signed in.
    void unsubscribing.then(
      () => {
        if (!signal.aborted) return;
        const auth = useAuthStore.getState();
        if (auth.hasSession && auth.accessToken) void subscribeToWebPush();
      },
      () => undefined,
    );
    await unsubscribing;
  } catch {
    /* best effort */
  }
}
